import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import { useTheme } from "next-themes";
import Iridescence from "./Iridescence";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useCart } from "../context/CartContext";
import { Copy, Upload } from "lucide-react";
import { IRIDESCENCE_DARK, IRIDESCENCE_LIGHT } from "../lib/iridescence";
import { formatPrice, isValidEmail } from "../lib/utils";

const MemoIridescence = React.memo(Iridescence);

const CheckoutTransferPage = () => {
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const { selectedPlan, items, email } = useCart();
  const isDark = resolvedTheme === "dark";
  const iridescenceColor = isDark ? IRIDESCENCE_DARK : IRIDESCENCE_LIGHT;
  const copyTimeoutRef = useRef(null);

  const cartTotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  );
  const totalWithPlan = cartTotal + (selectedPlan?.price || 0);
  const [copiedField, setCopiedField] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [orderNumber, setOrderNumber] = useState(null);
  const [previewOrderNumber, setPreviewOrderNumber] = useState(null);
  const bankData = {
    bank: "Mercado Pago",
    cbu: "0000003100080503594277",
    alias: "rgserviciotec",
    holder: "Rodrigo Cayetano García Ferreyra",
  };

  useEffect(() => () => {
    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current);
    }
  }, []);

  const handleCopy = async (value, field) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedField(null);
      }, 1500);
    } catch (error) {
      console.error("No se pudo copiar:", error);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result?.toString() || "";
        const base64 = result.split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setSendError("");
  };

  useEffect(() => {
    let isMounted = true;
    const loadPreview = async () => {
      try {
        const response = await fetch("/.netlify/functions/order-counter");
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted && typeof data?.nextOrderNumber === "number") {
          setPreviewOrderNumber(data.nextOrderNumber);
        }
      } catch (error) {
        // keep fallback display
      }
    };
    loadPreview();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!selectedFile) {
      setSendError("Seleccioná un comprobante para continuar.");
      return;
    }
    if (!isValidEmail(email)) {
      setSendError("Ingresá un email válido para recibir la confirmación.");
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      setSendError("El archivo supera los 5 MB.");
      return;
    }

    setIsSending(true);
    setSendError("");
    try {
      let nextOrderNumber = orderNumber;
      if (!nextOrderNumber) {
        const orderResponse = await fetch("/.netlify/functions/next-order-number", { method: "POST" });
        if (!orderResponse.ok) {
          const errText = await orderResponse.text();
          throw new Error(errText || "No se pudo generar el número de orden.");
        }
        const orderData = await orderResponse.json();
        nextOrderNumber = orderData?.orderNumber;
        if (!nextOrderNumber) {
          throw new Error("No se pudo generar el número de orden.");
        }
        setOrderNumber(nextOrderNumber);
      }
      const attachment = await fileToBase64(selectedFile);
      const payload = {
        orderNumber: nextOrderNumber,
        total: totalWithPlan,
        totalFormatted: formatPrice(totalWithPlan),
        plan: selectedPlan ? { name: selectedPlan.name, price: selectedPlan.price } : null,
        items: items.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          variantKey: item.variantKey,
        })),
        file: {
          filename: selectedFile.name,
          content: attachment,
          contentType: selectedFile.type,
        },
        customerEmail: email,
      };

      const response = await fetch("/.netlify/functions/send-transfer-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Error al enviar el comprobante.");
      }

      localStorage.setItem("rg_last_order", JSON.stringify({ orderNumber: nextOrderNumber, email }));
      navigate(`/checkout/transferencia/orden/${nextOrderNumber}`);
    } catch (error) {
      setSendError(error?.message || "No pudimos enviar el comprobante. Intentá de nuevo.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageLayout
      className="relative bg-gradient-to-br from-[#fff5e9] via-[#fffaf6] to-[#ffe3c4] dark:from-[#2a0e08] dark:via-[#3b140d] dark:to-[#1f0a08]"
      before={
        <div
          className={`absolute inset-0 pointer-events-none ${isDark ? "opacity-18" : "opacity-28"}`}
          key={`transfer-iridescence-${resolvedTheme}`}
        >
          <MemoIridescence color={iridescenceColor} speed={0.55} amplitude={isDark ? 0.1 : 0.12} />
        </div>
      }
    >
      <div className="relative z-10 mx-auto w-full max-w-2xl px-4 py-10 md:py-14">
        <div className="flex flex-col items-center text-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/checkout")}
            className="text-sm text-muted-foreground hover:text-foreground w-fit self-start"
          >
            &larr; Volver a checkout
          </button>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Pago por Transferencia
          </h1>
          <p className="text-sm text-muted-foreground">
            Orden #{orderNumber ?? previewOrderNumber ?? "--"} · Realizá la transferencia y enviá el comprobante
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <Card className="plan-card rounded-2xl shadow-lg bg-white/95 dark:bg-[#3a1b14]/90">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg md:text-xl">Resumen de tu compra</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-foreground">
              {selectedPlan && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">1x {selectedPlan.name}</span>
                  <span className="font-semibold">{formatPrice(selectedPlan.price)}</span>
                </div>
              )}
              {items.length ? (
                items.map((item) => (
                  <div key={`${item.id}-${item.variantKey}`} className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="font-semibold">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">No hay productos en el carrito.</p>
              )}
              <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold">
                <span>Total a transferir</span>
                <span className="text-foreground">{formatPrice(totalWithPlan)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="plan-card rounded-2xl shadow-lg bg-white/95 dark:bg-[#3a1b14]/90">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg md:text-xl">Datos bancarios</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-foreground">
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Banco / Billetera</p>
                  <p className="font-semibold">{bankData.bank}</p>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">CBU</p>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="font-semibold">{bankData.cbu}</p>
                    <div className="flex items-center gap-2">
                      {copiedField === "cbu" && (
                        <span className="text-xs font-medium text-emerald-600">Copiado</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopy(bankData.cbu, "cbu")}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white/80 text-muted-foreground transition hover:text-foreground hover:shadow-sm dark:bg-[#2e140f]"
                        aria-label="Copiar CBU"
                        title="Copiar CBU"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Alias</p>
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="font-semibold">{bankData.alias}</p>
                    <div className="flex items-center gap-2">
                      {copiedField === "alias" && (
                        <span className="text-xs font-medium text-emerald-600">Copiado</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopy(bankData.alias, "alias")}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white/80 text-muted-foreground transition hover:text-foreground hover:shadow-sm dark:bg-[#2e140f]"
                        aria-label="Copiar alias"
                        title="Copiar alias"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Titular</p>
                  <p className="font-semibold">{bankData.holder}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="plan-card rounded-2xl shadow-lg bg-white/95 dark:bg-[#3a1b14]/90">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg md:text-xl">Enviar comprobante</CardTitle>
              <p className="text-sm text-muted-foreground">
                Adjuntá el comprobante (JPG, PNG o PDF, máx 5 MB).
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/70 bg-white/80 px-4 py-8 text-center cursor-pointer dark:border-[#5a2a1b] dark:bg-[#2e140f]">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Hacé click para seleccionar el archivo
                </span>
                <Input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFileChange} />
                <span className="text-xs text-muted-foreground">
                  {selectedFile ? `Archivo: ${selectedFile.name}` : "Sin archivo seleccionado"}
                </span>
              </label>
              {sendError && (
                <p className="text-sm text-red-600" role="alert">
                  {sendError}
                </p>
              )}
              <Button
                onClick={handleSubmit}
                disabled={isSending || !selectedFile}
                className="w-full rounded-xl bg-[#00A1E4] text-white shadow-[0_8px_20px_rgba(0,161,228,0.35)] hover:bg-[#008FCB] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-[#00A1E4] dark:hover:bg-[#008FCB]"
              >
                {isSending ? "Enviando..." : "Enviar Comprobante"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default CheckoutTransferPage;
